/** The map editor's draft, and every edit that can be made to one.
 *
 * A draft is **not** a `NewGameSetup`. It could have been - the scenario format
 * is one - but a setup carries an `AdventureMapState` whose `moveCost` and
 * `occupant` planes are *derived*, and keeping a derived plane correct through
 * every brush stroke would mean the editor re-implementing `setTerrain` and
 * `setOccupant`. Those are rule modules the shell may not import (I10). So the
 * draft holds only what a person actually authors - terrain, objects, starts -
 * and the host compiles it into a setup through the kernel's own map builders
 * whenever it needs a real map. The compile is cheap and it happens on save, on
 * validate and on play; it does not happen while the brush is down.
 *
 * Everything here is pure and returns a new draft. That is what makes undo one
 * array of drafts rather than a command log with an inverse for each edit.
 */

import type { PlayerKind, TerrainKind } from "@game/core";
import { TERRAINS } from "@game/core";

// ------------------------------------------------------------------ shapes

export interface DraftMeta {
  readonly name: string;
  readonly description: string;
  readonly author: string;
}

/** The value types a `MapObjectState.payload` may hold. */
export type PayloadValue = number | string | boolean | null;
export type DraftPayload = Readonly<Record<string, PayloadValue>>;

export interface DraftObject {
  /** Stable within a draft, so React keys and selection survive an edit. */
  readonly key: string;
  /** A `MapObjectDef` id. The editor never invents one. */
  readonly def: string;
  readonly kind: string;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  /** A player id for a flagged object (a mine, a dwelling), or null. */
  readonly owner: string | null;
  /**
   * Overrides on top of the def's own payload.
   *
   * This is what makes a placed object a *thing* rather than a sprite: the def
   * says a Gold Mine yields 1000 a day and a Mystery Casket holds nothing, and
   * the map says otherwise. The kernel merges them the same way at read time
   * (`payloadOf` is `{...def.payload, ...state.payload}`), so an empty object
   * here means "exactly what the def says", which is the right default and the
   * one an author never has to think about.
   */
  readonly payload: DraftPayload;
}

export interface DraftPlace {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export interface DraftPlayer {
  readonly id: string;
  readonly kind: PlayerKind;
  readonly colour: string;
  readonly faction: string;
  /** Where this player's capital stands, or null for a player with no start. */
  readonly town: DraftPlace | null;
  readonly hero: DraftPlace | null;
}

export interface EditorDraft {
  readonly meta: DraftMeta;
  readonly seed: number;
  readonly width: number;
  readonly height: number;
  readonly levels: number;
  /** One entry per cell, `z * w * h + y * w + x`, indexing `TERRAINS`. */
  readonly terrain: Uint8Array;
  readonly objects: readonly DraftObject[];
  readonly players: readonly DraftPlayer[];
  readonly victory: string;
}

export const MIN_SIZE = 16;
/** The generator's ceiling, so a hand-authored map may be as big as a rolled one. */
export const MAX_SIZE = 288;
export const MAX_LEVELS = 2;

/** The eight the classic colours, in their canonical order. */
export const EDITOR_COLOURS: readonly string[] = Object.freeze([
  "red", "blue", "tan", "green", "orange", "purple", "teal", "pink",
]);

export const cellAt = (d: { width: number; height: number }, x: number, y: number, z: number): number =>
  (z * d.height + y) * d.width + x;

export const inDraft = (d: EditorDraft, x: number, y: number, z: number): boolean =>
  x >= 0 && y >= 0 && z >= 0 && x < d.width && y < d.height && z < d.levels;

export const terrainIndex = (kind: TerrainKind): number => {
  const i = TERRAINS.indexOf(kind);
  return i < 0 ? 0 : i;
};

export const terrainAtCell = (d: EditorDraft, x: number, y: number, z: number): TerrainKind =>
  TERRAINS[d.terrain[cellAt(d, x, y, z)] ?? 0] ?? "grass";

// ------------------------------------------------------------------ making

export function blankDraft(
  width = 48, height = 48, levels = 1, terrain: TerrainKind = "grass",
): EditorDraft {
  const n = width * height * levels;
  const plane = new Uint8Array(n).fill(terrainIndex(terrain));
  // The underworld starts as rock, because a second plane that begins as open
  // grass is a second plane the author has to erase before he can carve one.
  if (levels > 1) {
    const rock = terrainIndex("rock");
    plane.fill(rock, width * height, n);
  }
  return {
    meta: { name: "Untitled map", description: "", author: "" },
    seed: 1,
    width, height, levels,
    terrain: plane,
    objects: [],
    players: [
      newPlayer("p1", 0, "human"),
      newPlayer("p2", 1, "ai"),
    ],
    victory: "defeat_all",
  };
}

export function newPlayer(id: string, index: number, kind: PlayerKind = "ai"): DraftPlayer {
  return {
    id, kind,
    colour: EDITOR_COLOURS[index % EDITOR_COLOURS.length] ?? "red",
    faction: "sunspire",
    town: null,
    hero: null,
  };
}

/** Object keys are minted rather than derived from position: an object that is
 *  moved must stay the same object to the selection and to React. */
export function mintObjectKey(d: EditorDraft): string {
  let n = d.objects.length + 1;
  const used = new Set(d.objects.map((o) => o.key));
  while (used.has(`o${String(n)}`)) n += 1;
  return `o${String(n)}`;
}

// ------------------------------------------------------------------- edits

/**
 * Paint a disc of terrain.
 *
 * A disc rather than a square, because a square brush produces square coastline
 * and every map drawn with one looks drawn with one. `radius` 0 is a single
 * cell, which is what the smallest brush has to be for detail work.
 */
export function paintTerrain(
  d: EditorDraft, x: number, y: number, z: number, kind: TerrainKind, radius: number,
): EditorDraft {
  if (!inDraft(d, x, y, z)) return d;
  const value = terrainIndex(kind);
  const next = new Uint8Array(d.terrain);
  const r = radius < 0 ? 0 : radius;
  let changed = false;
  for (let dy = -r; dy <= r; dy++) {
    for (let dx = -r; dx <= r; dx++) {
      if (dx * dx + dy * dy > r * r + r) continue;
      const cx = x + dx, cy = y + dy;
      if (!inDraft(d, cx, cy, z)) continue;
      const i = cellAt(d, cx, cy, z);
      if (next[i] === value) continue;
      next[i] = value;
      changed = true;
    }
  }
  return changed ? { ...d, terrain: next } : d;
}

/** Fill a whole plane. The one edit that is faster than painting it. */
export function fillLevel(d: EditorDraft, z: number, kind: TerrainKind): EditorDraft {
  if (z < 0 || z >= d.levels) return d;
  const next = new Uint8Array(d.terrain);
  next.fill(terrainIndex(kind), z * d.width * d.height, (z + 1) * d.width * d.height);
  return { ...d, terrain: next };
}

export function addObject(
  d: EditorDraft, def: string, kind: string, x: number, y: number, z: number,
  owner: string | null = null, payload: DraftPayload = {},
): EditorDraft {
  if (!inDraft(d, x, y, z)) return d;
  const key = mintObjectKey(d);
  return { ...d, objects: [...d.objects, { key, def, kind, x, y, z, owner, payload }] };
}

export const draftObjectAt = (d: EditorDraft, x: number, y: number, z: number): DraftObject | null =>
  d.objects.find((o) => o.x === x && o.y === y && o.z === z) ?? null;

export function removeObjectAt(d: EditorDraft, x: number, y: number, z: number): EditorDraft {
  const hit = draftObjectAt(d, x, y, z);
  if (hit === null) return d;
  return { ...d, objects: d.objects.filter((o) => o.key !== hit.key) };
}

export function setObjectOwner(d: EditorDraft, key: string, owner: string | null): EditorDraft {
  return { ...d, objects: d.objects.map((o) => (o.key === key ? { ...o, owner } : o)) };
}

/**
 * Set or clear one payload key on one object.
 *
 * `null` as the *value* is a legitimate payload entry, so clearing is asked for
 * with `undefined` rather than with null. That distinction is the kernel's, not
 * this file's: `payloadOf` merges the def's payload with the object's, and an
 * explicit `null` on the object is how a map turns a def's value off.
 */
export function setObjectPayload(
  d: EditorDraft, key: string, field: string, value: PayloadValue | undefined,
): EditorDraft {
  if (field === "") return d;
  return {
    ...d,
    objects: d.objects.map((o) => {
      if (o.key !== key) return o;
      const next: Record<string, PayloadValue> = { ...o.payload };
      if (value === undefined) delete next[field]; else next[field] = value;
      return { ...o, payload: next };
    }),
  };
}

/** Replace an object's overrides wholesale; what a preset does. */
export function setObjectPayloadAll(
  d: EditorDraft, key: string, payload: DraftPayload,
): EditorDraft {
  return { ...d, objects: d.objects.map((o) => (o.key === key ? { ...o, payload } : o)) };
}

/**
 * Read a payload value back as the string an input holds, and parse it again.
 *
 * A payload is typed - the kernel's `payNum` and `payStr` are not
 * interchangeable and a mine whose `amount` arrives as `"1000"` yields nothing
 * - so the editor cannot simply keep every field as text. The rule is the
 * narrowest one that does not surprise: a value that reads as an integer is a
 * number, `true`/`false` are booleans, an empty box is a cleared key, and
 * everything else is a string.
 */
export function parsePayloadValue(text: string): PayloadValue | undefined {
  const t = text.trim();
  if (t === "") return undefined;
  if (t === "true") return true;
  if (t === "false") return false;
  if (t === "null") return null;
  if (/^-?\d+$/.test(t)) {
    const n = Number.parseInt(t, 10);
    if (Number.isSafeInteger(n)) return n;
  }
  return text;
}

export const payloadText = (v: PayloadValue): string =>
  (v === null ? "null" : String(v));

/**
 * Put a player's capital, or his starting hero, on a cell.
 *
 * A player has at most one of each, so this moves rather than adds. That is the
 * The classic rule and it is also the only one that keeps the editor honest: two
 * capitals for one player is a scenario `newGame` would build and nobody could
 * play.
 */
export function setPlayerTown(d: EditorDraft, id: string, at: DraftPlace | null): EditorDraft {
  return { ...d, players: d.players.map((p) => (p.id === id ? { ...p, town: at } : p)) };
}

export function setPlayerHero(d: EditorDraft, id: string, at: DraftPlace | null): EditorDraft {
  return { ...d, players: d.players.map((p) => (p.id === id ? { ...p, hero: at } : p)) };
}

export function patchPlayer(
  d: EditorDraft, id: string, patch: Partial<Omit<DraftPlayer, "id">>,
): EditorDraft {
  return { ...d, players: d.players.map((p) => (p.id === id ? { ...p, ...patch } : p)) };
}

export function addPlayer(d: EditorDraft): EditorDraft {
  if (d.players.length >= EDITOR_COLOURS.length) return d;
  const used = new Set(d.players.map((p) => p.id));
  let n = d.players.length + 1;
  while (used.has(`p${String(n)}`)) n += 1;
  return { ...d, players: [...d.players, newPlayer(`p${String(n)}`, d.players.length)] };
}

export function removePlayer(d: EditorDraft, id: string): EditorDraft {
  if (d.players.length <= 1) return d;
  return {
    ...d,
    players: d.players.filter((p) => p.id !== id),
    // An object flagged to a player who no longer exists would be owned by
    // nobody the game knows about; unflag rather than delete it.
    objects: d.objects.map((o) => (o.owner === id ? { ...o, owner: null } : o)),
  };
}

/**
 * Resize the board, keeping what is on it where it is.
 *
 * The top-left corner is the anchor rather than the centre, so growing a map
 * never moves what the author has already placed. Anything outside the new
 * bounds is dropped, and `draftIssues` will not have to report it because it is
 * gone rather than off the edge.
 */
export function resizeDraft(
  d: EditorDraft, width: number, height: number, levels: number,
  fill: TerrainKind = "rock",
): EditorDraft {
  const w = clamp(width, MIN_SIZE, MAX_SIZE);
  const h = clamp(height, MIN_SIZE, MAX_SIZE);
  const l = clamp(levels, 1, MAX_LEVELS);
  if (w === d.width && h === d.height && l === d.levels) return d;

  const next = new Uint8Array(w * h * l).fill(terrainIndex(fill));
  for (let z = 0; z < Math.min(l, d.levels); z++) {
    for (let y = 0; y < Math.min(h, d.height); y++) {
      for (let x = 0; x < Math.min(w, d.width); x++) {
        next[(z * h + y) * w + x] = d.terrain[cellAt(d, x, y, z)] ?? 0;
      }
    }
  }
  const inside = (p: DraftPlace | null): DraftPlace | null =>
    (p !== null && p.x < w && p.y < h && p.z < l ? p : null);

  return {
    ...d,
    width: w, height: h, levels: l,
    terrain: next,
    objects: d.objects.filter((o) => o.x < w && o.y < h && o.z < l),
    players: d.players.map((p) => ({ ...p, town: inside(p.town), hero: inside(p.hero) })),
  };
}

const clamp = (v: number, lo: number, hi: number): number =>
  (v < lo ? lo : v > hi ? hi : Math.trunc(v));

// -------------------------------------------------------------- validation

/**
 * Payload keys that are read off the DEFINITION and never off the instance.
 *
 * Two things price an object before anybody walks on it, and both of them read
 * `def.payload`: the AI's planner (`objectValue`, ai/adventure.ts) and the
 * player's own hover plate and info card (`objectEffect`, adventure/describe.ts).
 * Neither can read an instance payload, because `ObservedObject` does not carry
 * one - a payload is part of what the fog hides.
 *
 * The kernel, meanwhile, *does* honour the instance: `payloadOf` merges
 * `{...def.payload, ...state.payload}` at the moment of the visit. So an author
 * who writes `amount: 20000` onto one chest gets a chest that really pays
 * 20000, advertised by nobody as worth more than the 500 its definition says.
 *
 * That is a trap for the author rather than an unfairness between players -
 * the computer walks past it and the human is told the same wrong number - and
 * the answer is to say so rather than to forbid it. The keys are listed by kind
 * so the warning can name exactly which of an author's overrides are silent and
 * leave the rest alone: a Seer's quest, a timed event's day, a Black Market's
 * `stocked` flag are all instance payload and all resolved at visit time, and
 * none of them is priced in advance by anything.
 *
 * Two things a later reader needs before deciding to "fix" the plate instead.
 *
 * First, **this can only fire on a hand-authored map**. Nothing in `create.ts`
 * or `mapgen/place.ts` writes an instance `amount` onto a resource pile, so on
 * generator output the definition and the instance agree and the plate is
 * telling the truth. The divergence exists exactly where an author creates it.
 *
 * Second, **reading the definition is the correct behaviour, not a degraded
 * one**. The classic does not tell you how much a pile holds until you pick it up.
 * Making an object advertise its own contents would be a deliberate departure
 * from the game being cloned, and it would have to reach the hover plate and
 * the planner in the same change - publishing a value to the AI alone would
 * hand the computer something the player's own screen cannot see, which is the
 * inversion of I9 rather than an application of it.
 */
export const ADVERTISED_KEYS: Readonly<Record<string, readonly string[]>> = Object.freeze({
  resource: ["resource", "amount", "min", "max"],
  mine: ["resource", "amount"],
  dwelling: ["creature", "growth"],
  artifact: ["artifact"],
  treasure: ["gold", "experience"],
  bank: [
    "gold", "experience", "artifact", "resource", "amount", "rewardCreature", "rewardCount",
  ],
  shrine: ["spell"],
  scholar: ["primary", "amount"],
});

/** The overrides on this object that nothing will advertise, sorted. */
export function silentOverrides(o: DraftObject): readonly string[] {
  const advertised = ADVERTISED_KEYS[o.kind];
  if (advertised === undefined) return [];
  return Object.keys(o.payload).filter((k) => advertised.includes(k)).sort();
}

export type IssueLevel = "error" | "warning";

export interface DraftIssue {
  readonly level: IssueLevel;
  readonly text: string;
  /** Where to look, when the issue is about one place. */
  readonly at: DraftPlace | null;
}

/**
 * What is wrong with this map.
 *
 * The split matters: an **error** is a map the kernel will refuse or that
 * cannot be played, and the editor will not offer to play it. A **warning** is
 * a map that will start and probably should not have - one human and no
 * computer, a capital on an island, a player with a hero and no town. The
 * editor says so and lets the author do it anyway, because "probably should
 * not" is a judgement and the author is the one making it.
 */
export function draftIssues(d: EditorDraft, waterTerrains: ReadonlySet<string>): readonly DraftIssue[] {
  const out: DraftIssue[] = [];
  const err = (text: string, at: DraftPlace | null = null): void => {
    out.push({ level: "error", text, at });
  };
  const warn = (text: string, at: DraftPlace | null = null): void => {
    out.push({ level: "warning", text, at });
  };

  const playing = d.players.filter((p) => p.kind !== "none");
  if (playing.length === 0) err("Nobody is playing: every player is set to Absent.");
  if (playing.length === 1) {
    warn("Only one kingdom is playing. This is a sandbox, not a game that can be won.");
  }
  if (!playing.some((p) => p.kind === "human")) {
    warn("No human player. This map can only be watched.");
  }

  for (const p of playing) {
    if (p.town === null && p.hero === null) {
      err(`${p.colour} has neither a town nor a hero and is out on the first day.`);
    } else if (p.town === null) {
      warn(`${p.colour} starts with no town, and has seven days to take one.`);
    }
    for (const [what, place] of [["town", p.town], ["hero", p.hero]] as const) {
      if (place === null) continue;
      if (!inDraft(d, place.x, place.y, place.z)) {
        err(`${p.colour}'s ${what} is off the map.`, place);
        continue;
      }
      const kind = terrainAtCell(d, place.x, place.y, place.z);
      if (waterTerrains.has(kind)) {
        err(`${p.colour}'s ${what} is standing on ${kind}.`, place);
      }
    }
  }

  const colours = new Set<string>();
  for (const p of d.players) {
    if (colours.has(p.colour)) err(`Two players are both ${p.colour}.`);
    colours.add(p.colour);
  }

  const occupied = new Map<string, DraftObject>();
  for (const o of d.objects) {
    const key = `${String(o.x)},${String(o.y)},${String(o.z)}`;
    const clash = occupied.get(key);
    if (clash !== undefined) {
      err(`Two objects share (${String(o.x)}, ${String(o.y)}): ${clash.def} and ${o.def}.`,
        { x: o.x, y: o.y, z: o.z });
    }
    occupied.set(key, o);
  }

  for (const o of d.objects) {
    const silent = silentOverrides(o);
    if (silent.length === 0) continue;
    warn(
      `${o.def} at (${String(o.x)}, ${String(o.y)}) overrides ${silent.join(", ")}. `
      + "The kernel will honour it, but neither the computer's planner nor the "
      + "player's own hover plate reads an object's own payload - both price it "
      + "off the definition. Expect it to be walked past.",
      { x: o.x, y: o.y, z: o.z },
    );
  }

  if (d.objects.length === 0) {
    warn("There is nothing on this map to find, fight or capture.");
  }
  return out;
}

export const hasErrors = (issues: readonly DraftIssue[]): boolean =>
  issues.some((i) => i.level === "error");

// ----------------------------------------------------------------- history

/**
 * Undo, as one value.
 *
 * The first version of this was three `useState`s - draft, past, future - with
 * `undo` calling `setDraft` from inside the `setPast` updater. It typechecked,
 * it read plausibly, and it did not work: a React state updater runs during the
 * render phase and must be pure, so a `setState` nested inside one is not
 * reliably processed. The symptom was that Undo appeared to do nothing, and it
 * took a browser harness to find because no unit test in this workspace can
 * mount a component.
 *
 * So the whole history is one reducer over one value, and it is here rather
 * than in the component because that makes it a pure function a test can drive.
 */
export interface EditorHistory {
  readonly draft: EditorDraft;
  /** Oldest first; the last entry is what Undo returns to. */
  readonly past: readonly EditorDraft[];
  /** Newest first; the first entry is what Redo goes to. */
  readonly future: readonly EditorDraft[];
}

/**
 * Steps kept. A draft holds a whole terrain plane, and an unbounded history of
 * a 144x144 two-level map is about 80 MB of undo.
 */
export const MAX_HISTORY = 64;

export type EditorAction =
  | { readonly type: "commit"; readonly next: EditorDraft }
  | { readonly type: "undo" }
  | { readonly type: "redo" }
  /** Opening a file or generating a board: a new document, not an edit. */
  | { readonly type: "reset"; readonly draft: EditorDraft };

export const newHistory = (draft: EditorDraft): EditorHistory =>
  ({ draft, past: [], future: [] });

export function editorReducer(h: EditorHistory, a: EditorAction): EditorHistory {
  switch (a.type) {
    case "commit": {
      // An edit that changed nothing is not a step. Every edit above returns
      // the same object when it is a no-op, which is what makes this cheap.
      if (a.next === h.draft) return h;
      return {
        draft: a.next,
        past: [...h.past, h.draft].slice(-MAX_HISTORY),
        // A new edit after an undo abandons the redo branch, as everywhere.
        future: [],
      };
    }
    case "undo": {
      const last = h.past[h.past.length - 1];
      if (last === undefined) return h;
      return {
        draft: last,
        past: h.past.slice(0, -1),
        future: [h.draft, ...h.future].slice(0, MAX_HISTORY),
      };
    }
    case "redo": {
      const next = h.future[0];
      if (next === undefined) return h;
      return {
        draft: next,
        past: [...h.past, h.draft].slice(-MAX_HISTORY),
        future: h.future.slice(1),
      };
    }
    case "reset":
      return newHistory(a.draft);
  }
}

export const canUndo = (h: EditorHistory): boolean => h.past.length > 0;
export const canRedo = (h: EditorHistory): boolean => h.future.length > 0;
